/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * pieElement.js -- constructor for Dyn.PieElement() that creates a Pie Chart in the plot
 *
 * There can be multiple pies, one per X or Y-axis separation (cell), all of the same diameter.
 * There is one pie slice per case in each pie.
 * Pies are optionally sorted by an attribute, otherwise placed in case order.
 * Two pie types:
 * 	(1) frequency pie chart: each slice is the same size within each pie.
 * 	(2) value pie chart: each slice size is proportional to the numeric value of some attribute,
 * 		and non-numeric or non-positive values are removed from the pie.
 */

import { PlotElement } from './plotElement';

export const wrapDynPieElement = function(Dyn) {

    /**
     * A PieSection holds data for a group of cases of equal value within a pie.
     * Visually, the first case in the section covers the area for that section,
     * making it look like a single pie slice, and the remaining cases in the section are hidden.
     */
    class PieSection extends Dyn.PlotSection {
        constructor( category, isSum, arcStart ) {
            super( category, isSum );
            this.arcStart = arcStart;
            this.arcLength = 0;
        }
        static paramName = 'pieSections';
    }
    Dyn.PlotSection.registerSectionName( PieSection.paramName );

/**
 * Dyn.PieElement()
 */
class PieElement extends PlotElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsPieChart,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder ]
        }, Dyn );

        this.isValuePie = false; // set to true if pie slices proportioned to numeric values, otherwise they are proportioned to case counts.
        this.isSingleCaseValuePie = false; // if true, each pie slice is one case, otherwise multiple cases of same value are grouped into one slice.

        this[Dyn.EPlotControl.kControlsMultiCaseSections] = true;  // if true one icon per group of value attribute, if false one icon per case.
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.PIE_CHART;
    }

    get kUndoAction() {
        return Dyn.plotTitles.PIE_CHART;
    }

    toString() {
        return "{PieElement}";
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef ) {
        let xAxisView = subViewRef.xAxisView,
            yAxisView = subViewRef.yAxisView,
            topMargin = subViewRef.topMargin,
            rightMargin = subViewRef.rightMargin,
            valueAttribute = this.getValueAttribute( xAxisView, yAxisView ),
            isValueOnX = valueAttribute && (valueAttribute === xAxisView.getAttribute()),
            isValueOnY = valueAttribute && (valueAttribute === yAxisView.getAttribute()) && !isValueOnX;
        //hasSections = subViewRef.rightMargin.isColorAxis(),
        //isIncompleteChart = Boolean( ! hasSections );

        this.isValuePie = Boolean( valueAttribute );

        // update default title once on plot creation, then let user customize (don't change on recalc)
        this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ) );

        // update axis styling
        if( xAxisView.isPromptAxis() ) { // X is best place for value attribute, but either axis can be value
            xAxisView.setTitle( Dyn.loc( isValueOnY ? Dyn.plotStrings.promptGroup : ( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptValue ) ) );
        }
        if( yAxisView.isPromptAxis() ) { // Y is best place for group/categorical attribute, but either axis works
            yAxisView.setTitle( Dyn.loc( isValueOnX ? Dyn.plotStrings.promptGroup : ( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptGroup ) ) );
        }
        if( rightMargin.isNullAxis() ) {
            rightMargin.setTitle( Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptLegend ), true );
        }
        //xAxisView.setIncompleteAxis( false );
        //yAxisView.setIncompleteAxis( false );
        //rightMargin.setIncompleteAxis( isIncompleteChart );

        // disable unused axes: hide all numeric axis lines, and enable only one numeric attribute as the "value attribute"
        // categorical axes are used to form row and column bins
        let hideXAxisLine = xAxisView.isNumericActiveOrInactive(), // Pie never uses axis lines
            hideYAxisLine = yAxisView.isNumericActiveOrInactive(), // Pie never uses axis lines
            numAttributesUsedX = ((xAxisView.isCategoricalActiveOrInactive() || isValueOnX) ? 1 : 0), // use only 1 categorical or numeric on axis
            numAttributesUsedY = ((yAxisView.isCategoricalActiveOrInactive() || isValueOnY) ? 1 : 0); // use only 1 categorical or numeric on axis

        xAxisView.setPlotSpecificOptions( {
            "hideLinesLabels": hideXAxisLine,
            "numActiveAttributes": numAttributesUsedX
        } );
        yAxisView.setPlotSpecificOptions( {
            "hideLinesLabels": hideYAxisLine,
            "numActiveAttributes": numAttributesUsedY
        } );
    }

    /**
     * Record which cases that have missing values for the given plot.
     */
    calcMissingValueCases( numCases, xAxisView, yAxisView ) {
        var missingCases = this.plotShaper.dataSet.missingCases,
            valueAttribute = this.getValueAttribute( xAxisView, yAxisView ),
            caseValues = valueAttribute && valueAttribute.cases;
        const usePreFilterStats = true, // so that filtered-out categories are not marked as missing (they should be marked as filtered only)
            xAttrIndex = 0,
            yAttrIndex = 0;

        xAxisView.postCalcAxisUpdate(); // update axis type if changed during calcAxis()
        yAxisView.postCalcAxisUpdate();

        for( let i = 0; i < numCases; i++ ) {
            let x = xAxisView.getCaseDataAsNumber( i, xAttrIndex, usePreFilterStats ),
                y = yAxisView.getCaseDataAsNumber( i, yAttrIndex, usePreFilterStats ),
                // exclude missing values on X and Y (e.g. blank categorical value, non-numeric on numeric axis)
                isValid = isFinite( x ) && isFinite( y );
            if( valueAttribute && isValid ) {
                let value = caseValues[i];
                // exclude all but valid positive numbers on value axis
                isValid = Dyn.mathUtil.isFinite( value ) && Dyn.mathUtil.isPositive( value );
            }
            missingCases.setCaseIsMissing( i, !isValid );
        }
    }

    /**
     * Adjust the axis endpoints, depending on the bins in the histogram.
     * This step is take after CalcAxis() and CalcMissingValueCases() so that missing cases are correct.
     * @param numCases
     * @param subViewRef
     */
    calcPlotSpecificRange( numCases, subViewRef ) {
        var sortingAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
            useNumericIntervals = false;

        if( sortingAttribute ) {
            // create or update pie intervals
            subViewRef.rightMargin.postCalcAxisUpdate(); // first update stats to latest, due to changes from calcMissingValueCases()
            useNumericIntervals = sortingAttribute.setNumericIntervals( true );
            // if( useNumericIntervals ) {
            //     console.log( "PieElement numIntervals adjusted to " + sortingAttribute.numericIntervals.numIntervals,
            // 		sortingAttribute.numericIntervals.isAtMin ? "min" : sortingAttribute.numericIntervals.isAtMax ? "max" : "");
            // }
        }

        // set or clear flag so that views know plotShaper.hasElement( Dyn.EPlotControl.kControlsNumericIntervals ) is true or not
        this[Dyn.EPlotControl.kControlsNumericIntervals] = useNumericIntervals;

        // display or clear intervals and +- on legend
        subViewRef.topMargin.setPlotSpecificOptions( null );
        subViewRef.rightMargin.setPlotSpecificOptions( {
            useNumericIntervals: useNumericIntervals,
            showPlusMinus: useNumericIntervals,
            disablePlusButton: sortingAttribute?.numericIntervals?.isAtMax,
            disableMinusButton: sortingAttribute?.numericIntervals?.isAtMin,
            isIncompleteAxisStyle: !subViewRef.rightMargin.isColorAxis(),
            plusButtonTip: Dyn.loc("increase number of bins"),
            minusButtonTip: Dyn.loc("decrease number of bins"),
        } );
    }

    /**
     * Change icon style to Pie Slice (default is a circle)
     * @param ioPositions
     * @param xAxisView
     * @param yAxisView
     */
    calcIconStyle( /* ioPositions, xAxisView, yAxisView */ ) {
        "use strict";
        // set the style and pie radius
        this.plotShaper.iconStyler.setStyleSlice( this.calcPieRadius() );
    }

    /** update the case positional data (shared by all elements) and adornments (specific to this element) */
    calcPosition( ioPositions, xAxisView, yAxisView /*, ioAdornments */ ) {
        let iconStyler = this.plotShaper.iconStyler,
            dataSet = this.plotShaper.dataSet,
            xIsSeparated = xAxisView.isCategoricalActiveAxis(),
            yIsSeparated = yAxisView.isCategoricalActiveAxis(),
            plotCells = this.plotShaper.bins.cells,
            numCases = ioPositions.length,
            sortingAttribute = dataSet.getSelectedAttribute(),
            valueAttribute = this.getValueAttribute( xAxisView, yAxisView );

        // reset pie radius if needed; radius depends on bin size, which might not have been final when calcIconStyle() was called.
        let oldRadius = this.plotShaper.iconStyler.radius,
            newRadius = this.calcPieRadius();
        if( newRadius != oldRadius ) {
            this.plotShaper.iconStyler.setStyleSlice( this.calcPieRadius() );
        }

        // get case values, missing values, and bin locations {xBinIndex, yBinIndex, cellIndex}
        for( let i = 0; i < numCases; i++ ) {
            let p = ioPositions[i];
            p.caseIndex = i;
            p.x = xAxisView.getCaseDataAsNumber( i );
            p.y = yAxisView.getCaseDataAsNumber( i );
            p.isVisible = dataSet.isCaseNonMissingNonFiltered( p.caseIndex ); // see also setPositionHiddenInPlace()
            p.xAttrIndex = 0;
            p.yAttrIndex = 0;
            this.calcBinIndex( p, xIsSeparated, yIsSeparated );
            setPositionIfMissingOrFiltered( p ); // position all invisible cases offscreen
        }
        this.populateCellsWithCases( ioPositions );	// create cell data

        // position all visible cases as pie slices in each cell
        plotCells.forEach( cell => {
            this.createPieInCell( ioPositions, cell, sortingAttribute, valueAttribute, setPositionHiddenInPlace );
        });
        this.updateIsSingleCaseValuePie( plotCells );

        /** set the position of hidden cases to offscreen (to be animated on screen when status changes) */
        function setPositionIfMissingOrFiltered( p ) {
            if( !(p.isVisible) ) {
                p.x = iconStyler.missingValuePosition.x;
                p.y = iconStyler.missingValuePosition.y;
                p.r = Dyn.plotUtil.missingCaseSize;
            }
        }

        /** set the status of 2+ case in a slice as invisible but in the right place in pie, for later animation.  */
        function setPositionHiddenInPlace( p ) {
            p.isVisible = false; // mark icon as invisible (to optimize icons)
            p.r = Dyn.plotUtil.missingCaseSize; // change radius to zero to hide in place (like Dyn.BoxPlotElement.prototype.calcAllBoxPositions)
        }
    }

    /** update the case positional data, so PlotView can animate them into the new position */
    createPieInCell( ioPositions, cell, sortingAttribute, valueAttribute, setPositionHiddenInPlace ) {
        let numCasesInCell = cell.cases.length,
            oneIconPerSection = this[Dyn.EPlotControl.kControlsMultiCaseSections],
            cx = cell.centerX(),
            cy = cell.centerY(),
            sliceStart = 0,
            scale = Dyn.mathUtil.kTwoPi * Dyn.mathUtil.invertSafely( numCasesInCell ), // assumes frequency pie chart
            sliceValue = 1,
            section, sectionValue, prevSectionValue, pFirst;

        if( sortingAttribute ) {
            this.sortCasesInCell( cell, sortingAttribute, ioPositions, !sortingAttribute.isSortAscending );
        }
        if( valueAttribute ) {
            scale = Dyn.mathUtil.kTwoPi * Dyn.mathUtil.invertSafely( sumCaseValues( cell.cases ) );
        }

        Dyn.PlotSection.createSections( cell, PieSection.paramName ); // create cell.pieSections array
        for( let i = 0; i < numCasesInCell; i++ ) {
            let caseIndex = cell.cases[i];
            Dyn.assertArrayIndex( ioPositions, caseIndex );
            if( valueAttribute ) {
                sliceValue = valueAttribute.cases[caseIndex];
                Dyn.assert( isNumericPositive( sliceValue ) ); // if triggers, fix missing value elimination in calcPosition()
            }

            // set parameters for a slice {x,y,r,arcStart,arcLength}
            let p = ioPositions[caseIndex];
            p.x = cx;
            p.y = cy;
            p.r = this.plotShaper.iconStyler.radius;
            p.arcStart = sliceStart; // in Radians
            p.arcLength = scale * sliceValue; // in Radians
            sliceStart += p.arcLength;

            // set parameters for each pie section (1+ cases in each pie section)
            if( sortingAttribute ) {
                sectionValue = sortingAttribute.cases[caseIndex];
                if( sortingAttribute.hasNumericIntervals() ) {
                    sectionValue = sortingAttribute.getNumericIntervalString( caseIndex );
                }
            }
            if( i === 0 || sectionValue !== prevSectionValue ) {
                // with change in case value, start a new section (0th case always starts new section)
                section = createSectionFromSlice( p.arcStart, sectionValue, Boolean( valueAttribute ) );
                cell.pieSections.push( section );
                pFirst = p; // save reference to first icon in section for later
            }
            updateSectionFromSlice( section, p.arcLength, sliceValue );
            if( oneIconPerSection && p !== pFirst && pFirst ) {
                // this icon is for the 2nd+ icon in the group, and we want one icon per group
                pFirst.arcLength = section.arcLength; // expand arc length of visible icon
                setPositionHiddenInPlace( p ); // and hide this icon, in the right place for later restore
            }
            p.sectionIndex = cell.pieSections.length - 1; // store section index with icon
            prevSectionValue = sectionValue;
        }
        calcSectionsFinal( this.plotShaper.iconStyler, cell.pieSections );

        // sum of case values (to be used only if we have a "value pie" not frequency pie chart
        function sumCaseValues() {
            var sum = 0;
            cell.cases.forEach((value) => {
                sum += valueAttribute.cases[value]; // look up case value
            });
            Dyn.assert( cell.cases.length === 0 || isNumericPositive( sum ) );
            return sum;
        }

        function createSectionFromSlice( arcStart, sectionValue, isSum ) {
            let section = new PieSection( sectionValue, isSum, arcStart );
            if( valueAttribute ) {
                section.valueAttributeID = valueAttribute.id;
            }
            if( sortingAttribute ) {
                section.categoryAttributeID = sortingAttribute.id;
                //section.categoryIndex = sortingAttribute.stats.categories.indexOf( sectionValue );
            }
            return section;
        }

        function updateSectionFromSlice( section, arcLength, sliceValue ) {
            section.caseCount++;
            section.valueSum += sliceValue; // not used
            section.arcLength += arcLength;
        }

        // finalize pie section data
        function calcSectionsFinal( iconStyler, pieSections ) {
            const pieRadius = iconStyler.radius;

            pieSections.forEach( (section) => {
                // add data for placing labels
                let labelCenterPoint = iconStyler.calcPieSliceLabelCenterPoint( cx, cy, pieRadius, section.arcStart, section.arcLength );
                section.x = labelCenterPoint.x;
                section.y = labelCenterPoint.y;
                section.r = pieRadius;
                section.cx = cx;
                section.cy = cy;
            });
        }

        function isNumericPositive( v ) {
            return (Dyn.mathUtil.isFinite( v ) && Dyn.mathUtil.isPositive( v ));
        }
    }

    /**
     * Calculate the radius of our pie chart, assuming the cells created by X or Y axis separations
     * create pie charts all of the same radius.
     * @returns {number}
     */
    calcPieRadius() {
        "use strict";
        Dyn.assert( this.plotShaper.bins.cells.length > 0, "must populate cells first" );

        var stdCell = this.plotShaper.bins.cells[0], // assuming all cells are the same size
            kMargin = 10, // pixels
            cellDiameter = Math.min( stdCell.width, stdCell.height ) - kMargin;

        return (0.5 * cellDiameter);
    }

    /**
     * Do we want to show the counts for this pie chart?
     * Used by other plot elements to determine what pie chart is doing.
     * @returns {boolean}
     */
    allowPieSectionCounts() {
        "use strict";
        return true;  // for now we always want pie counts if showing a pie chart.
    }

    /**
     * Does this pie chart use a numeric attribute to determine the pie slice widths?
     * Used by other plot elements to determine what pie chart is doing.
     * @returns {boolean}
     */
    isValuePieChart() {
        "use strict";
        return this.isValuePie;  // for now we always want pie counts if showing a pie chart.
    }

    /**
     * Get the value attribute used with this Pie Chart, if any.
     * Used by other plot elements to determine what pie chart is doing.
     * @param {Dyn.AxisView} xAxisView
     * @param {Dyn.AxisView} yAxisView
     * @returns {Dyn.Attribute|null}
     */
    getValueAttribute( xAxisView, yAxisView ) {
        Dyn.assert( this.plotShaper.bins.cells.length > 0, "must populate cells first" );

        // check X then Y axis
        var valueAttribute = getAttributeIfValueAttribute( xAxisView );
        if( !valueAttribute ) {
            valueAttribute = getAttributeIfValueAttribute( yAxisView );
        }
        return valueAttribute;

        function getAttributeIfValueAttribute( axis ) {
            var valueAttribute = null;
            if( axis && axis.isNumericActiveOrInactive() ) {
                valueAttribute = axis.getAttribute();
            }
            return valueAttribute;
        }
    }

// show order by above legend
    displayOrderBy() {
        return false; // display the order by value always
    }

    allowCustomOrderBy() {
        return false;	// true if individual bars, not stacked/stretched bars
    }

    /**
     * Get any extra tool tip text for the given case.
     * Here the pie chart adds extra context for count and percent
     * only if this is an aggregate pie chart.
     * @param {number} caseIndex
     * @returns {string}
     */
    getExtraTooltipForCase( caseIndex, cellIndex, sectionIndex ) {
        Dyn.assert( this.plotShaper.bins.cells.length > 0, "must populate cells first" );
        var plotShaper = this.plotShaper,
            sectionText = '',
            hasCountOrPercent =
                plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionCount ) ||
                plotShaper.hasElement( Dyn.EPlotControl.kControlsSectionPercentage );

        if( hasCountOrPercent && cellIndex >= 0 && sectionIndex >= 0 ) {
            let cell = plotShaper.bins.cells[cellIndex];
            if( cell && cell.pieSections ) {
                let section = cell.pieSections[sectionIndex];
                Dyn.assertArrayIndex( cell.pieSections, sectionIndex, "invalid index to pie section" );
                sectionText = section.countPercentTip;
            }
        }
        return sectionText;
    }

    /**
     * Alows each element to respond to an axis button click
     * @param ioWhatChanged -- element should add a Dyn.EPlotChange parameter if returning true.
     * @param subViewRef
     * @param axisdata
     * @returns {boolean} -- true if handled by a Dyn.PlotElement
     */
    handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
        var handled = false,
            isPlusButton = (axisData.type === "plusButton"),
            isMinusButton = (axisData.type === "minusButton"),
            isOnLegend = (axisData.isLegend === true),
            sortingAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
            wantMoreIntervals = axisData.wantUndoOfClick ? !isPlusButton : isPlusButton;

        if( isOnLegend && (isPlusButton || isMinusButton) && sortingAttribute && sortingAttribute.hasNumericIntervals() ) {
            // update the number of intervals; new intervals will be calculated later by calcPlotSpecificRange()
            Dyn.plotUtil.setNumIntervalsPlusMinus( sortingAttribute.numericIntervals, wantMoreIntervals );
            ioWhatChanged[Dyn.EPlotChange.kChAxisSize] = true; // intervals affect axis range which affects axis view size.
            ioWhatChanged[Dyn.EPlotChange.kChIconPosition] = true; // icons must be positioned in new intervals
            handled = true;
            //console.log( "Pie numIntervals set to " + sortingAttribute.numericIntervals.numIntervals,
            //	sortingAttribute.numericIntervals.isAtMin ? "min" : sortingAttribute.numericIntervals.isAtMax ? "max" : "");
            // track analytics on histogram bin update
            _gaq.push( [ '_trackEvent', 'Plot Function', 'Click', "Selected Attribute numeric intervals updated" ] );
        }
        return handled;
    }

    /**
     * Get plot sonification data for the Histogram Graph
     * @returns {{attributeID: *, dataValues: [], notes: [], categories: [], type: string, dataMin: number, dataMax: number}}
     */
    getPlotSonificationData() {
        let plotCells = this.plotShaper.bins.cells,
            dataSet = this.plotShaper.dataSet,
            categoryAttribute = dataSet.getSelectedAttribute(),
            soundData = {
                type: 'categoryValues',
                categories: [], // category value for each note
                dataValues: [], // numeric value for each note
                dataMin: 0,
                dataMax: Dyn.mathUtil.kTwoPi, // max arc length and sum of arc lengths
                attributeID: categoryAttribute ? categoryAttribute.id : "",
                //numNotes : 0,
                notes: [],
            };

        // get non-missing case indices in line graph order, as sorted by calcAdornments()
        plotCells.forEach( ( cell ) => {
            if( cell.pieSections ) {
                cell.pieSections.forEach( ( section ) => {
                    let value = section.arcLength;
                    if( Dyn.mathUtil.isClose( value, 0 ) ) {
                        value = Dyn.dataUtil.kNumericMissingValue;
                    }
                    let note = Dyn.sound.makeNote().numericValueToNote(
                        value, soundData.dataMin, soundData.dataMax );
                    soundData.categories.push( section.category );
                    soundData.dataValues.push( value );
                    soundData.notes.push( note );
                } );
            }
        } );
        //soundData.numNotes = soundData.dataValues.length;
        return soundData;
    }

    /**
     *  Get the note to play for this section of the pie in the given axis cell.
     *  Return null if no pie-specific data to play.
     * @param caseIndex
     * @param cellIndex
     * @param sectionIndex
     * @returns {DataNote}
     */
    getSonificationDataForCase( caseIndex, cellIndex, sectionIndex ) {
        let plotShaper = this.plotShaper,
            noteData;

        if( cellIndex >= 0 && sectionIndex >= 0 ) {
            let cell = plotShaper.bins.cells[cellIndex],
                section = cell && cell.pieSections && cell.pieSections[sectionIndex];
            if( section ) {
                let value = section.arcLength,
                    min = 0,
                    max = Dyn.mathUtil.kTwoPi;
                noteData = Dyn.sound.makeNote().numericValueToNote( value, min, max );
            }
        }
        return noteData;
    }

    updateIsSingleCaseValuePie( plotCells ) {
        // set flag if pie slices are one case each, or grouped by value
        this.isSingleCaseValuePie = true;
        plotCells.forEach( (cell) => cell.pieSections.forEach( ( section ) => {
            if( section.caseCount > 1 ) {
                this.isSingleCaseValuePie = false;
            }
        } ) );
        // set flag in each section for single-case or multi-case pie
        plotCells.forEach( (cell) => cell.pieSections.forEach( ( section ) => {
            section.isSingleCaseValuePie = this.isSingleCaseValuePie;
        } ) );
    }

} // class PieElement

Dyn.PieElement = PieElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPieChart, Dyn.PieElement );
//Dyn.PieElement.inheritsFrom( Dyn.PlotElement );

}; // wrapDynPieElement
